// REG-802: read-only stat line at the bottom of a column's three-dot menu.
import { useEffect, useState } from 'react';
import { useGridMenuItem, type CustomMenuItemProps } from 'ag-grid-react';
import type { GetMainMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { queryClient } from '../../utils/constants';
import { columnStatErrorText, type ColumnStatResult } from '../../utils/columnStats';
import './ColumnStatMenuItem.css';

export interface ColumnStatSource {
  /** Identifies the aggregate: source, column and every filter in effect. */
  queryKey: readonly unknown[];
  load: () => Promise<ColumnStatResult>;
}

interface ColumnStatMenuItemParams {
  source: ColumnStatSource;
}

// Totals follow live data but a menu reopened a moment later should not refetch.
const STAT_STALE_TIME = 60_000;

function ColumnStatMenuItem(props: CustomMenuItemProps & { source: ColumnStatSource }) {
  const { source } = props;
  // No hover, keyboard focus or click handling: it is a read-out, not a command.
  useGridMenuItem({ configureDefaults: () => false });

  const [state, setState] = useState<{ lines: string[]; pending: boolean }>(() => {
    const cached = queryClient.getQueryData<ColumnStatResult>(source.queryKey);
    return cached ? { lines: cached.lines, pending: false } : { lines: ['Calculating…'], pending: true };
  });

  useEffect(() => {
    let cancelled = false;
    queryClient
      .fetchQuery({ queryKey: source.queryKey, queryFn: source.load, staleTime: STAT_STALE_TIME })
      .then((result) => { if (!cancelled) setState({ lines: result.lines, pending: false }); })
      .catch((error) => { if (!cancelled) setState({ lines: [columnStatErrorText(error)], pending: false }); });
    return () => { cancelled = true; };
  }, [source]);

  return (
    <div className="sky-column-stat" aria-live="polite" aria-busy={state.pending}>
      {state.lines.map((line) => <div key={line}>{line}</div>)}
    </div>
  );
}

/**
 * The grid's default column menu with the stat line under Reset Columns.
 * `source` is null for columns without a stat (actions, JSON), which keep the
 * plain menu.
 */
export function columnMenuWithStat(
  params: GetMainMenuItemsParams,
  source: ColumnStatSource | null,
): (string | MenuItemDef)[] {
  if (!source) return params.defaultItems;
  const item: MenuItemDef = {
    name: 'Column stat',
    menuItem: ColumnStatMenuItem,
    menuItemParams: { source } satisfies ColumnStatMenuItemParams,
    suppressCloseOnSelect: true,
  };
  return [...params.defaultItems, 'separator', item];
}
